import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { AppConfig, SectionKey } from "../types";
import { fetchAppConfig } from "../utils/api";

/**
 * Admin-controlled configuration, loaded once after login.
 *
 * The defaults below are what the app did before settings existed, so a failed
 * or slow /config request degrades to "everything on" rather than to a blank
 * app. The server enforces the real rules regardless of what we render.
 */
export const DEFAULT_CONFIG: AppConfig = {
  sections: { selection: true, study: true, history: true, games: true, chat: true },
  // Courses are the one place the fallback is NOT permissive: showing «Омбре
  // Броус» as open for the moment before /config lands would flash a course
  // that is still being recorded.
  courses: { base: true, ombre: false },
  blocks: [],
  free_selection_limit: 5,
  free_lessons_per_section: 1,
  maintenance_mode: false,
  maintenance_message_ru: "",
  maintenance_message_en: "",
  contact_handle: "",
  announcement: { enabled: false, text_ru: "", text_en: "" },
  pricing_notice: { enabled: false, text_ru: "", text_en: "" },
  currency: "BYN",
  currency_en: "$",
  byn_per_100_rub: 3.66,
  plans: [],
};

interface Ctx {
  config: AppConfig;
  /** Re-read after an admin saves, so their own app reflects the change at once. */
  reload: () => void;
}

const AppConfigContext = createContext<Ctx>({ config: DEFAULT_CONFIG, reload: () => {} });

export function AppConfigProvider({
  token, children,
}: {
  token: string | null;
  children: React.ReactNode;
}) {
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);

  const reload = useCallback(() => {
    if (!token) return;
    fetchAppConfig(token)
      .then(setConfig)
      .catch(() => { /* keep the permissive defaults; the API still enforces */ });
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <AppConfigContext.Provider value={{ config, reload }}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig(): Ctx {
  return useContext(AppConfigContext);
}

/** Is this menu section switched on? Admins always see everything. */
export function useSectionEnabled(key: SectionKey, isAdmin = false): boolean {
  const { config } = useAppConfig();
  return isAdmin || config.sections[key] !== false;
}
